// The hero's facts on story's conditions (the spike's own source): `hero.region` (the region the layout says the hero
// stands in: the innermost), `hero.in.<region>` (inside that region's shape, nested ones included) and
// `hero.dist.<spot>` (metres on the ground to a placement, a story spawn group or a townsperson). Shared by the story
// layer and the content check, so both know the same facts.
import type { FactSource } from "@rpg/story";

/** A region's shape as the layout gives it. */
export interface RegionShape {
  kind: "circle" | "rect" | "polygon";
  x?: number;
  z?: number;
  r?: number;
  x0?: number;
  z0?: number;
  x1?: number;
  z1?: number;
  points?: readonly (readonly number[])[];
}

/** Point-in-shape for the layout's region shapes. */
export function inShape(s: RegionShape, x: number, z: number): boolean {
  if (s.kind === "circle") return Math.hypot(x - (s.x ?? 0), z - (s.z ?? 0)) <= (s.r ?? 0);
  if (s.kind === "rect") return x >= (s.x0 ?? 0) && x <= (s.x1 ?? 0) && z >= (s.z0 ?? 0) && z <= (s.z1 ?? 0);
  const p = s.points ?? [];
  let inside = false;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const xi = p[i]![0]!;
    const zi = p[i]![1]!;
    const xj = p[j]![0]!;
    const zj = p[j]![1]!;
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

/** A region's middle (a circle's centre, a rect's middle, a polygon's mean point). */
export function shapeCentre(s: RegionShape): [number, number] {
  if (s.kind === "circle") return [s.x ?? 0, s.z ?? 0];
  if (s.kind === "rect") return [((s.x0 ?? 0) + (s.x1 ?? 0)) / 2, ((s.z0 ?? 0) + (s.z1 ?? 0)) / 2];
  const p = s.points ?? [];
  return [p.reduce((a, q) => a + q[0]!, 0) / p.length, p.reduce((a, q) => a + q[1]!, 0) / p.length];
}

/** The hero source. `spots`: placement, group and townsperson ids with their ground positions. */
export function heroFacts(o: { regions: readonly { id: string; shape: RegionShape }[]; spots: ReadonlyMap<string, readonly [number, number]>; hero(): { x: number; z: number }; regionAt(x: number, z: number): string | null }): FactSource {
  return {
    name: "hero",
    get: (k) => {
      const p = o.hero();
      if (k === "region") return o.regionAt(p.x, p.z);
      if (k.startsWith("in.")) return inShape(o.regions.find((r) => r.id === k.slice(3))!.shape, p.x, p.z);
      if (k.startsWith("dist.")) {
        const q = o.spots.get(k.slice(5))!;
        return Math.hypot(p.x - q[0], p.z - q[1]);
      }
      return null;
    },
    knows: (k) => k === "region" || (k.startsWith("in.") && o.regions.some((r) => r.id === k.slice(3))) || (k.startsWith("dist.") && o.spots.has(k.slice(5))),
  };
}
